import axios from 'axios';
import { Badge, Button, Icon, Table } from 'components/lib';
import { LoaderWithText } from 'components/loaderWithText/LoaderWithText';
import { TableNoCaptalize } from 'components/tableNoCaptalize/table';
import { UnlimitedCard } from 'components/unlimitedCard/card';
import DateUtils from 'helpers/dateUtils';
import InputUtils from 'helpers/inputUtils';
import NumberUtils from 'helpers/numberUtils';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import LoaderImage from './images/loading.gif';
import styles from './styles.module.scss';
import { stateOptions } from 'data';
import {
  buildPolicyPayload,
  hasLivePolicyData,
} from './reviewPolicyHelpers';

const dateUtils = new DateUtils();
const inputUtils = new InputUtils();
const numberUtils = new NumberUtils();

export function ReviewPolicy() {
  const location = useLocation();
  const { state } = location;
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [payload, setPayload] = useState('');
  const [coverages, setCoverages] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [trailers, setTrailers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [openedIssues, setOpenedIssues] = useState(false);
  const [hasLiveSubmissionDetails, setHasLiveSubmissionDetails] = useState(true);

  async function runAPI() {
    try {
      const quoteResponse = await axios.get('/api/quote/' + state.quote_id);
      const internalQuote = quoteResponse.data.data;
      const localQuote = internalQuote?.quote || {};

      let liveSubmission = null;
      let liveSubmissionAvailable = false;

      if (state?.data?.submission_number && state.data.submission_number !== 'N/A') {
        try {
          const response = await axios.get(
            '/api/quote/info/' + state.data.submission_number
          );
          liveSubmission = response.data.data;
          liveSubmissionAvailable = hasLivePolicyData(liveSubmission);
        } catch (error) {
          console.log(error);
        }
      }

      const formatted_documents = [];
      const formatted_coverages = [];
      const formatted_vehicles = [];
      const formatted_trailers = [];
      const formatted_drivers = [];
      let temporary_total = liveSubmissionAvailable
        ? 0
        : Number(internalQuote?.premium || 0);

      if (liveSubmissionAvailable) {
        if (liveSubmission.coverages?.al) {
          temporary_total += Number(liveSubmission.coverages.al.premium);
        }

        if (liveSubmission.coverages?.apd) {
          temporary_total += Number(liveSubmission.coverages.apd.premium);
        }

        if (liveSubmission.coverages?.mtc) {
          temporary_total += Number(liveSubmission.coverages.mtc.premium);
        }

        if (liveSubmission.coverages?.tgl) {
          temporary_total += Number(liveSubmission.coverages.tgl.premium);
        }

        if (liveSubmission.coverages?.ntl) {
          temporary_total += Number(liveSubmission.coverages.ntl.premium);
        }

        if (liveSubmission.documents?.new_business) {
          Object.keys(liveSubmission.documents.new_business).forEach((key) => {
            if (key !== 'pfa_pdf') {
              formatted_documents.push({
                key,
                value: liveSubmission.documents.new_business[key],
              });
            }
          });
        }

        if (liveSubmission.coverages) {
          Object.keys(liveSubmission.coverages).forEach((key) => {
            const effective_date =
              liveSubmission.coverages[key].policy_effective_date.split('-');

            formatted_coverages.push({
              name: key
                .replace('al', 'Auto Liability')
                .replace('apd', 'Physical Damage')
                .replace('mtc', 'Motor Truck Cargo')
                .replace('ntl', 'Non-Trucking Liability')
                .replace('tgl', 'General Liability'),
              limit:
                key === 'tgl'
                  ? numberUtils.formatToCurrency(1000000)
                  : numberUtils.formatToCurrency(
                      liveSubmission.coverages[key].limit
                    ),
              premium: numberUtils.formatToCurrency(
                liveSubmission.coverages[key].premium
              ),
              'Effective Date': `${effective_date[0]}-${effective_date[1]}-${effective_date[2]}`,
              'Expiration Date':
                liveSubmission.coverages[key].policy_expiration_date,
              carrier: liveSubmission.coverages[key].carrier,
              PIP:
                key === 'al'
                  ? liveSubmission.coverages[key].opt_pip
                    ? 'Y'
                    : 'N'
                  : '',
              UM:
                key === 'al'
                  ? liveSubmission.coverages[key].opt_um
                    ? 'Y'
                    : 'N'
                  : '',
              'Policy Number': liveSubmission.coverages[key].policy_number
                ? liveSubmission.coverages[key].policy_number
                : 'N/A',
              status: liveSubmission.coverages[key]
                .policy_pending_cancelation_date
                ? 'pending cancellation'
                : 'active',
            });
          });
        }
      }

      const liveVehicles = liveSubmissionAvailable ? liveSubmission?.vehicles || [] : [];
      const localVehicles = localQuote?.vehicles || [];
      const localTrailers = localQuote?.trailers || [];
      const localDrivers = localQuote?.drivers || [];

      if (liveVehicles.length > 0) {
        liveVehicles.forEach((key) => {
          if (key.equipment_type === 'Vehicle') {
            if (!formatted_vehicles.some((veh) => veh.VIN === key.vin)) {
              formatted_vehicles.push({
                VIN: key.vin,
                make: key.make,
                year: key.year,
                model: key.model,
                value: numberUtils.formatToCurrency(key.value),
                'Body type': key.body_type.replaceAll('_', ' '),
                Deductible: key.deductible
                  ? numberUtils.formatToCurrency(key.deductible)
                  : 0,
              });
            }
          } else if (key.equipment_type === 'Trailer') {
            if (!formatted_trailers.some((veh) => veh.VIN === key.vin)) {
              formatted_trailers.push({
                VIN: key.vin,
                make: key.make,
                year: key.year,
                model: key.model,
                value: numberUtils.formatToCurrency(key.value),
                Class: key.class,
                'Body type': key.body_type.replaceAll('_', ' '),
                Deductible: key.deductible
                  ? numberUtils.formatToCurrency(key.deductible)
                  : 0,
                'Loss Payee': key.loss_payee
                  ? numberUtils.formatToCurrency(key.loss_payee)
                  : 0,
              });
            }
          }
        });
      } else {
        localVehicles.forEach((key) => {
          formatted_vehicles.push({
            VIN: key.vin,
            make: key.make,
            year: key.year,
            model: key.model,
            value: numberUtils.formatToCurrency(key.value),
            'Body type': String(key.bodyType || '').replaceAll('_', ' '),
            Deductible: key.deductible
              ? numberUtils.formatToCurrency(key.deductible)
              : 0,
          });
        });

        localTrailers.forEach((key) => {
          formatted_trailers.push({
            VIN: key.vin,
            make: key.make,
            year: key.year,
            model: key.model,
            value: numberUtils.formatToCurrency(key.value),
            Class: key.class || '',
            'Body type': String(key.bodyType || '').replaceAll('_', ' '),
            Deductible: key.deductible
              ? numberUtils.formatToCurrency(key.deductible)
              : 0,
            'Loss Payee': key.loss_payee
              ? numberUtils.formatToCurrency(key.loss_payee)
              : 0,
          });
        });
      }

      const liveDrivers = liveSubmissionAvailable ? liveSubmission?.drivers || [] : [];
      if (liveDrivers.length > 0) {
        liveDrivers.forEach((key) => {
          const licenseState = stateOptions.find(
            (item) => item.label === key.driver_license_state
          )
            ? stateOptions.find(
                (item) => item.label === key.driver_license_state
              ).value
            : stateOptions.find(
                (item) => item.value === key.driver_license_state
              )?.name;

          formatted_drivers.push({
            'First Name': key.first_name,
            'Last Name': key.last_name,
            'License State': licenseState || key.driver_license_state,
            'Date of Birth': dateUtils.isoDate(
              new Date(key.date_of_birth).toISOString()
            ),
            'Date of Hire': dateUtils.isoDate(
              new Date(key.date_of_hire).toISOString()
            ),
            'Years of experience': key.years_experience,
          });
        });
      } else {
        localDrivers.forEach((key) => {
          formatted_drivers.push({
            'First Name': key.firstName,
            'Last Name': key.lastName,
            'License State': key.licenseState,
            'Date of Birth': key.dateOfBirth
              ? dateUtils.isoDate(key.dateOfBirth)
              : '',
            'Date of Hire': key.dateOfHire
              ? dateUtils.isoDate(key.dateOfHire)
              : '',
            'Years of experience': key.yearsExperience,
          });
        });
      }

      setCoverages(formatted_coverages);
      setVehicles(formatted_vehicles);
      setTrailers(formatted_trailers);
      setDrivers(formatted_drivers);
      setTotal(temporary_total);
      setHasLiveSubmissionDetails(liveSubmissionAvailable);

      if (liveSubmissionAvailable) {
        await axios.patch('/api/quote/' + state.quote_id, {
          premium: temporary_total,
        });
      }

      const mergedPayload = buildPolicyPayload({
        internalQuote,
        liveSubmission: liveSubmissionAvailable ? {
          ...liveSubmission,
          documents: formatted_documents,
        } : null,
      });

      setPayload(mergedPayload);
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  }

  useEffect(() => {
    runAPI();
  }, []);

  if (loading) {
    return <LoaderWithText />;
  }

  return (
    <>
      {state &&
        state.data.notifications &&
        state.data.notifications.length >= 1 && (
          <div
            style={{
              backgroundColor: '#FFFDE6',
              marginBottom: 15,
              padding: 15,
              borderRadius: 8,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              flexDirection: 'column',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <div
                style={{
                  backgroundColor: '#D7A700',
                  marginRight: 10,
                  width: 34,
                  height: 34,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 34,
                }}
              >
                <Icon
                  image="alert-triangle"
                  color="light"
                  size={20}
                />
              </div>
              <span style={{ color: '#D7A700', marginTop: 3 }}>
                We have detected an issue that requires your
                attention:{' '}
                <span
                  style={{
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  }}
                  onClick={() => setOpenedIssues(!openedIssues)}
                >
                  CLICK HERE
                </span>{' '}
                to view issues.
              </span>
            </div>
            {openedIssues && (
              <div
                style={{
                  marginLeft: 40,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {state.data.notifications.map((value) => {
                  return (
                    <span
                      style={{
                        color: '#D7A700',
                        marginTop: 2,
                        marginBottom: 2,
                      }}
                    >
                      •{' '}
                      {inputUtils.capitalizeFirstLetter(
                        value.alert.replaceAll('_', ' ')
                      )}{' '}
                      - {value.notes}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        )}

      <div className={styles.wrapper}>
        <UnlimitedCard className="mb-6 w-100% mx-auto" id="14">
          {!hasLiveSubmissionDetails && (
            <div
              style={{
                backgroundColor: '#FFFDE6',
                marginBottom: 15,
                padding: 15,
                borderRadius: 8,
                color: '#A16207',
              }}
            >
              Live Cover Whale policy details are temporarily unavailable. Showing the saved policy snapshot instead.
            </div>
          )}
          <div className={styles.infos}>
            <div className={styles.info}>
              <div className={styles.title}>Company Name</div>
              <div className={styles.value}>
                {payload &&
                payload.insuredInformation &&
                payload.insuredInformation.legal_name
                  ? payload.insuredInformation.legal_name
                  : 'Not Found'}
              </div>
            </div>
            <div className={styles.info}>
              <div className={styles.title}>DOT Number</div>
              <div className={styles.value}>
                {payload && payload.dot_number
                  ? payload.dot_number
                  : 'Not Found'}
              </div>
            </div>
            <div className={styles.info}>
              <div className={styles.title}>Effective Date</div>
              <div className={styles.value}>
                {payload && payload.effective_date
                  ? dateUtils.isoDate(payload.effective_date)
                  : 'Not Found'}
              </div>
            </div>
            <div className={styles.info}>
              <div className={styles.title}>Total Premium</div>
              <div className={styles.value}>
                {payload
                  ? numberUtils.formatToCurrency(total || payload.premium || 0)
                  : '$00,000'}
              </div>
            </div>
            <div className={styles.info}>
              <div className={styles.title}>Policy Status</div>
              <div className={styles.value}>
                {payload &&
                  payload.status &&
                  payload.status === 'bound' && (
                    <Badge text={'Active'} color={'green'} />
                  )}
                {payload &&
                  payload.status &&
                  payload.status === 'pending_cancelation' && (
                    <Badge
                      text={'Pending Cancellation'}
                      color={'yellow'}
                    />
                  )}
              </div>
            </div>
          </div>

          {payload && hasLiveSubmissionDetails && (
            <div
              className="border rounded p-4 mt-4 border-slate-300"
              style={{ paddingBottom: 20 }}
            >
              <div className="flex justify-between font-bold pb-4 pt-2 border-b border-slate-300">
                <p>Coverages</p>
              </div>
              <Table
                data={coverages}
                withoutHours
                hide={['id', 'company_id', 'submission_number']}
                loading={loading}
                badge={{
                  col: 'status',
                  color: 'grey',
                  condition: [
                    { value: 'active', color: 'green' },
                    { value: 'pending cancelation', color: 'yellow' },
                  ],
                }}
              />
            </div>
          )}

          {payload && vehicles.length > 0 && (
            <div
              className="border rounded p-4 mt-4 border-slate-300"
              style={{ paddingBottom: 20 }}
            >
              <div className="flex justify-between font-bold pb-4 pt-2 border-b border-slate-300">
                <p>Vehicles</p>
              </div>
              <Table
                data={vehicles}
                badge={{ col: 'Body type', color: 'gray' }}
                loading={loading}
              />
            </div>
          )}

          {payload && trailers.length > 0 && (
            <div
              className="border rounded p-4 mt-4 border-slate-300"
              style={{ paddingBottom: 20 }}
            >
              <div className="flex justify-between font-bold pb-4 pt-2 border-b border-slate-300">
                <p>Trailers</p>
              </div>
              <Table
                data={trailers}
                badge={{ col: 'Body type', color: 'gray' }}
                loading={loading}
              />
            </div>
          )}

          {payload && drivers.length > 0 && (
            <div
              className="border rounded p-4 mt-4 border-slate-300"
              style={{ paddingBottom: 20 }}
            >
              <div className="flex justify-between font-bold pb-4 pt-2 border-b border-slate-300">
                <p>Drivers</p>
              </div>
              <TableNoCaptalize
                data={drivers}
                badge={{ col: 'Body type', color: 'gray' }}
                loading={loading}
              />
            </div>
          )}

          {payload && hasLiveSubmissionDetails && (
            <div
              className="border rounded p-4 mt-4 border-slate-300"
              style={{ width: '100%' }}
            >
              <div
                className="font-bold pb-4 pt-2 border-b border-slate-300"
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignContent: 'center',
                }}
              >
                <p>Documents</p>
                {payload.submissionStatus.status ===
                  'Quote Docs in Progress' && (
                  <p>
                    <img
                      src={LoaderImage}
                      alt="Image"
                      width={30}
                      height={30}
                    />
                  </p>
                )}
              </div>
              {payload.submissionStatus.status ===
              'Quote Docs in Progress' ? (
                <h2 className="capitalize text-xl text-center font-semibold pt-4">
                  It may take up to 3 minutes for the documents to
                  appear
                </h2>
              ) : (
                <>
                  {payload.documents.map((value, index) => {
                    return (
                      <div
                        className="flex justify-between py-4 border-b border-slate-300"
                        key={index}
                      >
                        <p>
                          {value.key
                            .replaceAll('_', ' ')
                            .toUpperCase()
                            .replaceAll('PDF', '')}
                        </p>
                        <div className="flex gap-4 mr-4">
                          <Button
                            icon="download-cloud"
                            size={10}
                            action={() => {
                              window.open(value.value);
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          )}

          {/*     {payload && !payload.error && (
            <div
              className="border rounded p-4 mt-4 border-slate-300"
              style={{ width: "100%" }}
            >
              <div
                className="font-bold pb-4 pt-2 border-b border-slate-300"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignContent: "center",
                }}
              >
                <p>Endorsement #1</p>
              </div>
              <div
                className="font-bold pb-4 pt-2 border-b border-slate-300"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignContent: "center",
                  marginTop: 10,
                }}
              >
                <p>Additions</p>
              </div>

              <div className="flex justify-between py-4 border-b border-slate-300">
                <div style={{ display: "flex", alignItems: "center" }}>
                  <div style={{ marginRight: 5 }}>
                    <Icon image="plus" size={20} color="#5bbe25" />
                  </div>
                  Added a new vehicle (3FRNF65B78V069803)
                </div>
              </div>

              <div className="flex justify-between py-4 border-b border-slate-300">
                <div style={{ display: "flex", alignItems: "center" }}>
                  <div style={{ marginRight: 5 }}>
                    <Icon image="plus" size={20} color="#5bbe25" />
                  </div>
                  Added a new driver (Lorenzo Preciado)
                </div>
              </div>

              <div className="flex justify-between py-4 border-b border-slate-300">
                <div style={{ display: "flex", alignItems: "center" }}>
                  <div style={{ marginRight: 5 }}>
                    <Icon image="plus" size={20} color="#5bbe25" />
                  </div>
                  Added a new trailer (GHRNF15B78V068562)
                </div>
              </div>

              <div
                className="font-bold pb-4 pt-2 border-b border-slate-300"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignContent: "center",
                  marginTop: 10,
                }}
              >
                <p>Removals</p>
              </div>

              <div className="flex justify-between py-4 border-b border-slate-300">
                <div style={{ display: "flex", alignItems: "center" }}>
                  <div style={{ marginRight: 5 }}>
                    <Icon image="minus" size={20} color="#C93415" />
                  </div>
                  Removed a driver (Igor Jacques)
                </div>
              </div>
            </div>
          )} */}
        </UnlimitedCard>
      </div>
    </>
  );
}
